import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AdminLayout } from '@/app/layouts';
import { AuthGuard } from './guards';
import { LoadingState } from '@/components/feedback';
import { APP_ROUTES } from '@/config/constants';
import type { RouteHandle } from '@/lib/hooks';

// Lazy load pages for code splitting
const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage'));
const BudgetsPage = lazy(() => import('@/features/budgets/pages/BudgetsPage'));
const PartiesPage = lazy(() => import('@/features/parties/pages/PartiesPage'));
const PaymentJustificationsPage = lazy(
  () => import('@/features/payment-justifications/pages/PaymentJustificationsPage')
);
const EditPaymentJustificationPage = lazy(
  () => import('@/features/payment-justifications/pages/tabs/EditPaymentJustificationPage')
);
const PaymentJustificationGeneralPage = lazy(
  () => import('@/features/payment-justifications/pages/tabs/PaymentJustificationGeneralPage')
);
const PaymentJustificationBeneficiariesPage = lazy(
  () => import('@/features/payment-justifications/pages/tabs/PaymentJustificationBeneficiariesPage')
);
const EmployeesPage = lazy(() => import('@/features/employees/pages/EmployeesPage'));
const PayrollsPage = lazy(() => import('@/features/payrolls/pages/PayrollsPage'));
const SettingsPage = lazy(() => import('@/features/settings/pages/SettingsPage'));
const GeneralSettingsPage = lazy(() => import('@/features/settings/pages/GeneralSettingsPage'));
const MembersSettingsPage = lazy(() => import('@/features/settings/pages/MembersSettingsPage'));
const ParametersSettingsPage = lazy(() => import('@/features/settings/pages/ParametersSettingsPage'));
const BudgetMappingsSettingsPage = lazy(() => import('@/features/settings/pages/BudgetMappingsSettingsPage'));
const BankAccountsSettingsPage = lazy(() => import('@/features/settings/pages/BankAccountsSettingsPage'));
const TemplatesSettingsPage = lazy(() => import('@/features/settings/pages/TemplatesSettingsPage'));
const ProfilePage = lazy(() => import('@/features/profile/pages/ProfilePage'));
const PersonalInfoPage = lazy(() => import('@/features/profile/pages/PersonalInfoPage'));
const SignInPage = lazy(() => import('@/features/auth/pages/SignInPage'));
const NotFoundPage = lazy(() => import('@/features/auth/pages/NotFoundPage'));
const ForbiddenPage = lazy(() => import('@/features/auth/pages/ForbiddenPage'));

// Suspense wrapper for lazy loaded components
function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LoadingState />}>{children}</Suspense>;
}

// Helper to create route handle with breadcrumb
const handle = (breadcrumb: RouteHandle['breadcrumb']): RouteHandle => ({ breadcrumb });

export const router = createBrowserRouter([
  // Admin routes (protected)
  {
    path: '/',
    element: (
      <AuthGuard>
        <AdminLayout />
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        handle: handle('Dashboard'),
        element: (
          <LazyPage>
            <DashboardPage />
          </LazyPage>
        ),
      },
      {
        path: 'partidas-presupuestarias',
        handle: handle('Partidas Presupuestarias'),
        element: (
          <LazyPage>
            <BudgetsPage />
          </LazyPage>
        ),
      },
      {
        path: 'terceros',
        handle: handle('Terceros'),
        element: (
          <LazyPage>
            <PartiesPage />
          </LazyPage>
        ),
      },
      {
        path: 'justificaciones-pago',
        handle: handle('Justificaciones de Pago'),
        children: [
          {
            index: true,
            element: (
              <LazyPage>
                <PaymentJustificationsPage />
              </LazyPage>
            ),
          },
          {
            path: ':id',
            handle: handle((params) => `SPI #${params['id']}`),
            element: (
              <LazyPage>
                <EditPaymentJustificationPage />
              </LazyPage>
            ),
            children: [
              {
                index: true,
                element: <Navigate to="general" replace />,
              },
              {
                path: 'general',
                handle: handle('General'),
                element: (
                  <LazyPage>
                    <PaymentJustificationGeneralPage />
                  </LazyPage>
                ),
              },
              {
                path: 'beneficiarios',
                handle: handle('Beneficiarios'),
                element: (
                  <LazyPage>
                    <PaymentJustificationBeneficiariesPage />
                  </LazyPage>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'empleados',
        handle: handle('Empleados'),
        element: (
          <LazyPage>
            <EmployeesPage />
          </LazyPage>
        ),
      },
      {
        path: 'roles-pago',
        handle: handle('Roles de Pago'),
        element: (
          <LazyPage>
            <PayrollsPage />
          </LazyPage>
        ),
      },
      {
        path: 'configuraciones',
        handle: handle('Configuraciones'),
        element: (
          <LazyPage>
            <SettingsPage />
          </LazyPage>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="general" replace />,
          },
          {
            path: 'general',
            handle: handle('General'),
            element: (
              <LazyPage>
                <GeneralSettingsPage />
              </LazyPage>
            ),
          },
          {
            path: 'directorio',
            handle: handle('Directorio'),
            element: (
              <LazyPage>
                <MembersSettingsPage />
              </LazyPage>
            ),
          },
          {
            path: 'parametros',
            handle: handle('Parámetros'),
            element: (
              <LazyPage>
                <ParametersSettingsPage />
              </LazyPage>
            ),
          },
          {
            path: 'partidas',
            handle: handle('Partidas'),
            element: (
              <LazyPage>
                <BudgetMappingsSettingsPage />
              </LazyPage>
            ),
          },
          {
            path: 'cuentas-bancarias',
            handle: handle('Cuentas Bancarias'),
            element: (
              <LazyPage>
                <BankAccountsSettingsPage />
              </LazyPage>
            ),
          },
          {
            path: 'plantillas',
            handle: handle('Plantillas'),
            element: (
              <LazyPage>
                <TemplatesSettingsPage />
              </LazyPage>
            ),
          },
        ],
      },
      {
        path: 'perfil',
        handle: handle('Mi Perfil'),
        element: (
          <LazyPage>
            <ProfilePage />
          </LazyPage>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="datos-personales" replace />,
          },
          {
            path: 'datos-personales',
            handle: handle('Datos Personales'),
            element: (
              <LazyPage>
                <PersonalInfoPage />
              </LazyPage>
            ),
          },
        ],
      },
    ],
  },

  // Public routes
  {
    path: APP_ROUTES.SIGNIN,
    element: (
      <LazyPage>
        <SignInPage />
      </LazyPage>
    ),
  },

  // 403 - Forbidden
  {
    path: APP_ROUTES.FORBIDDEN,
    element: (
      <LazyPage>
        <ForbiddenPage />
      </LazyPage>
    ),
  },

  // 404 - Not Found
  {
    path: APP_ROUTES.NOT_FOUND,
    element: (
      <LazyPage>
        <NotFoundPage />
      </LazyPage>
    ),
  },

  // Catch all - redirect to 404
  {
    path: '*',
    element: <Navigate to={APP_ROUTES.NOT_FOUND} replace />,
  },
]);
